import React, { useState } from 'react';
import { useUsers } from '../context/UsersContext';
import { C, S } from '../utils/styles';
import HealthCuesLogo from './HealthCuesLogo';

export default function Login() {
  const { login, completeFirstLogin } = useUsers();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // First-time password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [isEmployerContact, setIsEmployerContact] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const validatePassword = (pwd) => {
    if (pwd.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(pwd)) return 'Password must contain an uppercase letter';
    if (!/[0-9]/.test(pwd)) return 'Password must contain a number';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const result = await login(email, password);
      
      if (!result.success) {
        setError(result.error);
        setLoading(false);
        return;
      }

      // Check if user needs to change password (first login)
      if (result.requiresPasswordChange) {
        setPendingUser(result.user);
        setIsEmployerContact(result.isEmployerContact || false);
        setShowPasswordChange(true);
        setLoading(false);
        return;
      }

      // Normal login success
      setLoading(false);
    } catch (error) {
      setError('An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate password
    const validationError = validatePassword(newPassword);
    if (validationError) {
      setError(validationError);
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      // Complete first login (pass employer contact flag and clientId)
      // For employer contacts, clientId is stored in pendingUser.clients[0]
      const clientId = isEmployerContact ? (pendingUser.clients?.[0] || null) : null;
      const success = await completeFirstLogin(
        pendingUser.id, 
        newPassword,
        isEmployerContact,
        clientId
      );
      
      if (success) {
        // User is now logged in
        setShowPasswordChange(false);
        setLoading(false);
      } else {
        setError('Failed to set password. Please try again.');
        setLoading(false);
      }
    } catch (error) {
      setError('An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  // First-time password change screen
  if (showPasswordChange && pendingUser) {
    return (
      <div data-testid="password-change-screen" style={{
        minHeight: '100vh',
        background: C.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        padding: 20,
      }}>
        <div style={{
          ...S.card,
          width: '100%',
          maxWidth: 400,
          padding: '40px 36px',
          textAlign: 'center',
        }}>
          <div style={{ marginBottom: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <HealthCuesLogo size={56} />
            <h1 style={{ fontSize: 20, fontWeight: 800, color: C.text, margin: 0 }}>
              Welcome, {pendingUser.firstName}!
            </h1>
            <p style={{ fontSize: 13, color: C.textMid, margin: 0 }}>
              Please set your password to get started.
            </p>
          </div>

          <form onSubmit={handlePasswordChange} style={{ display: 'flex', flexDirection: 'column', gap: 16, textAlign: 'left' }}>
            <div>
              <label htmlFor="new-password" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>
                New Password *
              </label>
              <input
                id="new-password"
                name="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: 13,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  background: C.bg,
                  color: C.text,
                }}
                required
              />
            </div>

            <div>
              <label htmlFor="confirm-password" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>
                Confirm Password *
              </label>
              <input
                id="confirm-password"
                name="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: 13,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  background: C.bg,
                  color: C.text,
                }}
                required
              />
            </div>

            <div style={{ fontSize: 11, color: C.textMid, background: C.blueLight, padding: 10, borderRadius: 6 }}>
              Password must be at least 8 characters with one uppercase, one number, and one symbol.
            </div>

            {error && (
              <div style={{ padding: 10, background: '#fee', borderRadius: 6, color: '#c00', fontSize: 12, fontWeight: 600 }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              style={{
                padding: '12px 24px',
                fontSize: 13,
                fontWeight: 700,
                color: '#fff',
                background: C.green,
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                marginTop: 8,
              }}
            >
              Set Password & Continue
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Normal login screen
  return (
    <div data-testid="login-screen" style={{
      minHeight: '100vh',
      background: C.bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      padding: 20,
    }}>
      <div style={{
        ...S.card,
        width: '100%',
        maxWidth: 400,
        padding: '40px 36px',
        textAlign: 'center',
      }}>
        <div style={{ marginBottom: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <HealthCuesLogo size={56} />
          <h1 style={{ fontSize: 20, fontWeight: 800, color: C.text, margin: 0, letterSpacing: '-0.02em' }}>
            HealthCues Client Success Engine
          </h1>
          <p style={{ fontSize: 12, color: C.textLight, margin: 0 }}>
            Sign in to access the optimization platform
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
          <div style={{ marginBottom: 14 }}>
            <label htmlFor="login-email" style={S.label}>Email</label>
            <input
              id="login-email"
              name="email"
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              autoComplete="email"
              style={{ ...S.input, fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: 13 }}
              required
            />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label htmlFor="login-password" style={S.label}>Password</label>
            <input
              id="login-password"
              name="password"
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              autoComplete="current-password"
              style={{ ...S.input, fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: 13 }}
              required
            />
          </div>

          {error && (
            <div data-testid="login-error" style={{
              padding: '8px 12px',
              background: C.redLight,
              borderRadius: 8,
              fontSize: 12,
              color: C.red,
              fontWeight: 600,
              marginBottom: 14,
              textAlign: 'center',
            }}>
              {error}
            </div>
          )}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '10px 18px',
              background: C.accent,
              color: '#fff',
              borderRadius: 8,
              border: 'none',
              fontSize: 13,
              fontWeight: 700,
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.7 : 1,
              transition: 'opacity 0.15s',
            }}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>

          <p style={{ fontSize: 11, color: C.textMid, marginTop: 16, textAlign: 'center' }}>
            Forgot your password? Contact your administrator.
          </p>
        </form>
      </div>
    </div>
  );
}
